<app-spinner-overlay [showSpinner]="isLoading" />
@if (loadErrorMessage) {
    <div class="container my-3 vstack gap-3">
        <div class="alert alert-danger mb-0" role="alert">{{ loadErrorMessage }}</div>
        <button type="button" class="btn btn-primary" (click)="loadBookingDetails()">Try again</button>
        <button type="button" class="btn btn-outline-secondary" (click)="returnToBooking()">Return to booking</button>
    </div>
}
<ng-container *ngIf="bookingContext$ | async as context">
    <div
        class="container my-3 vstack gap-3"
        [ngClass]="context.venue.shouldUseVenueStorefrontExperience()
            ? 'col-lg-10 col-xl-8' : 'col-sm-10 col-md-8 col-lg-6 col-xl-4'"
        [formGroup]="form"
        appVenueColour
        [primaryColour]="context.venue.primaryColour"
        data-testid="modify-booking"
    >
        <h1 class="h4 fw-semibold m-0">Modify Booking</h1>
        @if (context.venue.shouldUseVenueStorefrontExperience()) {
            <fieldset class="vstack gap-3" [disabled]="isLoading">
                <legend class="visually-hidden">Reschedule your booking</legend>
                <nav class="d-flex justify-content-between gap-2" aria-label="Booking modification">
                    @if (areaGroups) {
                        <button type="button" class="btn btn-outline-secondary" (click)="back()"
                            data-testid="back-modification-step">Back</button>
                    }
                    <button type="button" class="btn btn-outline-secondary ms-auto" (click)="returnToBooking()"
                        data-testid="exit-modification">Exit without changes</button>
                </nav>
                @if (context.bookingDetails.isDateRestricted) {
                    <div class="alert alert-info mb-0" role="note">
                        The date and party size cannot be changed under this booking's payment restrictions.
                        Please contact {{ context.venue.displayName }}
                        @if (context.venue.phoneNumber; as phoneNumber) {
                            on <a [href]="'tel:' + phoneNumber">{{ phoneNumber }}</a>
                        }
                        to discuss changes.
                    </div>
                }
                @if (summary; as summary) {
                    <section class="vstack gap-3" aria-labelledby="review-booking-changes">
                        <h2 #reviewHeading id="review-booking-changes" class="h5 fw-semibold m-0" tabindex="-1">
                            Review your changes
                        </h2>
                        <app-venue-storefront-booking-summary-card [summary]="summary" />
                        @if (!hasStorefrontChanges(context.bookingDetails)) {
                            <p class="mb-0" role="status">You haven't changed your booking. Go back to make changes, or exit without changes.</p>
                        }
                        <button type="button" class="btn btn-primary" data-testid="confirm-storefront-modification"
                            [disabled]="!hasStorefrontChanges(context.bookingDetails)"
                            (click)="confirmStorefrontChanges(context.bookingDetails)">
                            Confirm Changes
                        </button>
                    </section>
                } @else if (areaGroups) {
                    @for (group of areaGroups; track $index) {
                        @if (availability?.hasBookingSlotsInsideCutOffTimeForAreaId(group.area?.id)) {
                            <div class="alert alert-info mb-0" data-testid="modification-booking-notice" role="alert">
                                <div class="hstack gap-3 justify-content-center text-center">
                                    <div class="hstack gap-2">
                                        <i class="bi bi-info-circle-fill text-info" aria-hidden="true"></i>
                                    </div>
                                    <div class="fw-semibold">
                                        @if (group.area) {
                                            <span>{{ group.area.displayName }}: </span>
                                        }
                                        @if (availability!.venue.shortPreBookingWindowMessage; as message) {
                                            <span [innerHTML]="message"></span>
                                        } @else {
                                            @if (availability!.smallestActivePreBookingWindow(group.area?.id); as preBookingWindow) {
                                                To book less than {{ preBookingWindow | duration : DurationUnit.Minute }} in advance,
                                                please get in touch at
                                                @if (availability!.venue.phoneNumber; as phoneNumber) {
                                                    <a [href]="'tel:' + phoneNumber">{{ phoneNumber }}</a>
                                                }
                                                @if (availability!.venue.emailAddress; as emailAddress) {
                                                    @if (availability!.venue.phoneNumber) { or }
                                                    <a [href]="'mailto:' + emailAddress">{{ emailAddress }}</a>
                                                }.
                                            }
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    }
                    <app-venue-storefront-booking-slot-step
                        [groups]="areaGroups"
                        [idealTime]="slotIdealTime!"
                        [selectedSlot]="currentSlot"
                        (slotSelected)="selectReplacement($event, context.bookingDetails, context.venue)"
                    />
                } @else {
                    <app-venue-storefront-booking-search
                        [venue]="context.venue"
                        [initialValues]="searchCriteria"
                        [updatingBooking]="context.bookingDetails"
                        (searchAvailability)="searchAvailability($event, context.bookingDetails, context.venue)"
                    />
                }
            </fieldset>
        } @else {
            <app-modal-booking-form
                [organisationId]="organisationId"
                [businessId]="businessId"
                [venueId]="venueId"
                [bookingDetails]="context.bookingDetails"
                (saveRequested)="save($event[0], context.bookingDetails)"
            />
        }
        @if (errorMessage) {
            <div class="alert alert-danger mb-0" role="alert" data-testid="modification-error">{{ errorMessage }}</div>
        }
    </div>
</ng-container>
